import { motion } from 'framer-motion';
import { Lock, Shield, Zap, Search, Eye, FileCheck } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../context/LanguageContext';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: 'easeOut' },
};

const staggerContainer = {
  animate: {
    transition: { staggerChildren: 0.1 },
  },
};

export default function Trust() {
  const { t } = useLanguage();

  const securityFeatures = [
    {
      icon: Lock,
      title: t('trustPage.sec1'),
      description: t('trustPage.sec1Desc'),
      color: 'emerald',
    },
    {
      icon: Shield,
      title: t('trustPage.sec2'),
      description: t('trustPage.sec2Desc'),
      color: 'blue',
    },
    {
      icon: Zap,
      title: t('trustPage.sec3'),
      description: t('trustPage.sec3Desc'),
      color: 'amber',
    },
    {
      icon: Search,
      title: t('trustPage.sec4'),
      description: t('trustPage.sec4Desc'),
      color: 'purple',
    },
  ];

  const colorClasses = {
    emerald: {
      bg: 'bg-emerald-100',
      icon: 'text-emerald-600',
      border: 'border-emerald-200',
    },
    blue: {
      bg: 'bg-blue-100',
      icon: 'text-blue-600',
      border: 'border-blue-200',
    },
    amber: {
      bg: 'bg-amber-100',
      icon: 'text-amber-600',
      border: 'border-amber-200',
    },
    purple: {
      bg: 'bg-purple-100',
      icon: 'text-purple-600',
      border: 'border-purple-200',
    },
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 py-20 md:py-28" data-testid="trust-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.p variants={fadeInUp} className="text-sm font-medium text-blue-400 uppercase tracking-wider mb-3">
              {t('trustPage.subtitle')}
            </motion.p>
            <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-tight">
              {t('trustPage.title')}
            </motion.h1>
          </motion.div>
        </div>
      </section>

      {/* Security Features Section */}
      <section className="py-20 md:py-28" data-testid="trust-security">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
              {t('trustPage.securityTitle')}
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {securityFeatures.map((feature, index) => {
                const Icon = feature.icon;
                const colors = colorClasses[feature.color];
                return (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    className={`p-8 bg-white rounded-3xl border ${colors.border}`}
                    data-testid={`trust-feature-${index + 1}`}
                  >
                    <div className={`w-14 h-14 rounded-2xl ${colors.bg} flex items-center justify-center mb-6`}>
                      <Icon className={`w-7 h-7 ${colors.icon}`} />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Privacy Section */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="trust-privacy">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          >
            <motion.div variants={fadeInUp}>
              <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mb-6">
                <Eye className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                {t('trustPage.privacyTitle')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {t('trustPage.privacyText')}
              </p>
            </motion.div>
            <motion.div variants={fadeInUp} className="relative">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.pexels.com/photos/6289038/pexels-photo-6289038.jpeg"
                  alt="Security shield concept"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-20 md:py-28" data-testid="trust-compliance">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="max-w-3xl mx-auto text-center"
          >
            <motion.div variants={fadeInUp} className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mb-6 mx-auto">
              <FileCheck className="w-8 h-8 text-emerald-600" />
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              {t('trustPage.complianceTitle')}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-slate-600 leading-relaxed">
              {t('trustPage.complianceText')}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-20 md:py-28 bg-slate-900" data-testid="trust-indicators">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            <motion.div variants={fadeInUp} className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-emerald-400 mb-2">256-bit</div>
              <div className="text-sm text-slate-400">{t('trustPage.indicator1')}</div>
            </motion.div>
            <motion.div variants={fadeInUp} className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-blue-400 mb-2">24/7</div>
              <div className="text-sm text-slate-400">{t('trustPage.indicator2')}</div>
            </motion.div>
            <motion.div variants={fadeInUp} className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-amber-400 mb-2">99.9%</div>
              <div className="text-sm text-slate-400">{t('trustPage.indicator3')}</div>
            </motion.div>
            <motion.div variants={fadeInUp} className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-purple-400 mb-2">0</div>
              <div className="text-sm text-slate-400">{t('trustPage.indicator4')}</div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
